import {
  ApiResponse,
  CompanyFull,
  CurrentInvestment,
  ExternalNews,
  Fund,
  InvestorFinanceNews,
  InvestorFullInfo,
  InvestorSummary,
  IpoSummary,
  LeadingVCCompany,
  LPInvestment,
  PageApiRespone,
  PageApiRespone as PageApiResponse,
  PastInvestment,
  People,
  InvestorType,
  InvestorLink,
  Following,
  FundDetail,
  LpDetail,
  InvestorSearchCondition,
  InvestorSearchConditionQuery,
  SearchInvestors,
} from '../../types'
import { basePath, callAPI, callAPIRaw, callAuthAPI } from './common'
import * as next from 'next'
import queryString from 'query-string'
import { CompanyData, OverseasCompanyData } from '../../types/api'
import { printError } from '../log'

export const fetchInvestors = async (
  condition: InvestorSearchCondition,
  fetchApi: <T>(url: string) => Promise<T>
): Promise<SearchInvestors> => {
  try {
    const formattedCondition: InvestorSearchConditionQuery = {
      limit: condition.limit,
      offset: condition.offset,
    }

    if (condition.keywords.length > 0) {
      formattedCondition.keywords = condition.keywords
    }

    if (condition.categories.length > 0) {
      formattedCondition.categories = condition.categories
        ?.map((v) => {
          if (v.subCategories.length !== 0) {
            return v.name + ':' + v.subCategories.join('_')
          } else {
            return v.name
          }
        })
        .join(',')
    }

    if (condition.countries.length > 0) {
      formattedCondition.countries = condition.countries.join(',')
    }

    if (condition.industries.length > 0) {
      formattedCondition.industries = condition.industries.join(',')
    }
    const result = await getInvestors(formattedCondition, fetchApi)

    return {
      data: result.data || [],
      count: result.count || 0,
      master: result.master || { categories: [] },
    }
  } catch (e: any) {
    printError(e)

    return {
      data: [],
      count: 0,
      master: { categories: [] },
    }
  }
}

export const fetchInvestorSummary = async (
  ctx: next.NextPageContext,
  shortname: string
): Promise<ApiResponse<InvestorSummary | undefined> | undefined> => {
  let res
  let statusCode
  try {
    res = await getInvestorSummary(ctx, shortname)
    statusCode = res.status
  } catch (e: any) {
    printError(e)

    if (e.response instanceof Response) {
      statusCode = e.response.status
    }
  }
  return { data: res?.data, status: statusCode }
}

const getInvestors = async (
  condition: InvestorSearchConditionQuery,
  fetchApi: <T>(url: string) => Promise<T>
) => {
  return fetchApi<SearchInvestors>(
    `${basePath}/investors?${queryString.stringify(condition)}`
  )
}

const getInvestorSummary = async (
  ctx: next.NextPageContext,
  shortname: string
) => {
  return callAuthAPI<ApiResponse<InvestorSummary>>(
    ctx,
    `${basePath}/pages/investors/${shortname}/summary`
  )
}

export const getInvestorFull = async (shortname: string) => {
  return callAPI<ApiResponse<InvestorFullInfo>>(
    `${basePath}/pages/investors/${shortname}`
  )
}

export const getInvestorLink = async (shortname: string) => {
  return callAPI<ApiResponse<InvestorLink>>(
    `${basePath}/investors/${shortname}`
  )
}

export const getInvestorCompanies = async (
  shortname: string,
  limit: number,
  offset: number
) => {
  return callAPI<PageApiResponse<CompanyData[]>>(
    `${basePath}/investors/${shortname}/companies?limit=${limit}&offset=${offset}`
  )
}

export const getInvestorOverseasCompanies = async (
  globalId: string,
  limit: number,
  offset: number
) => {
  return callAPI<PageApiResponse<OverseasCompanyData[]>>(
    `${basePath}/investors/${globalId}/overseas_companies?limit=${limit}&offset=${offset}`
  )
}

type InvestorCompaniesDownloadFormat = 'xlsx'
export const downloadInvestorCompanies = async (
  shortname: string,
  format: InvestorCompaniesDownloadFormat
) => {
  return callAPIRaw(`${basePath}/investors/${shortname}/companies.${format}`, {
    method: 'GET',
    credentials: 'include',
  })
}

export const getSeedFunded = async (
  shortname: string,
  limit: number,
  offset: number
) => {
  return callAPI<PageApiResponse<CompanyFull[]>>(
    `${basePath}/investors/${shortname}/companies-seed?limit=${limit}&offset=${offset}`
  )
}

export const getAcquisitions = async (
  shortname: string,
  limit: number,
  offset: number
) => {
  return callAPI<PageApiResponse<CompanyFull[]>>(
    `${basePath}/investors/${shortname}/companies-acquisition?limit=${limit}&offset=${offset}`
  )
}

export const getPartnerships = async (
  shortname: string,
  limit: number,
  offset: number
) => {
  return callAPI<PageApiResponse<CompanyFull[]>>(
    `${basePath}/investors/${shortname}/companies-partnership?limit=${limit}&offset=${offset}`
  )
}

export const getInvestorFunds = async (
  shortname: string,
  limit: number,
  offset: number
) => {
  return callAPI<PageApiResponse<Fund[]>>(
    `${basePath}/investors/${shortname}/funds?limit=${limit}&offset=${offset}`
  )
}

export const getDetailsCurrent = async (
  shortname: string,
  limit: number,
  offset: number
) => {
  return callAPI<PageApiResponse<CurrentInvestment[]>>(
    `${basePath}/investors/${shortname}/investments-current?limit=${limit}&offset=${offset}`
  )
}

export const getDetailsPast = async (
  shortname: string,
  limit: number,
  offset: number
) => {
  return callAPI<PageApiResponse<PastInvestment[]>>(
    `${basePath}/investors/${shortname}/investments-past?limit=${limit}&offset=${offset}`
  )
}

export const getDetailsLeading = async (
  shortname: string,
  limit: number,
  offset: number
) => {
  return callAPI<PageApiResponse<LeadingVCCompany[]>>(
    `${basePath}/investors/${shortname}/investments-leading?limit=${limit}&offset=${offset}`
  )
}

export const getDetailsIpo = async (
  shortname: string,
  limit: number,
  offset: number
) => {
  return callAPI<PageApiResponse<IpoSummary[]>>(
    `${basePath}/investors/${shortname}/investments-ipo?limit=${limit}&offset=${offset}`
  )
}

export const getDetailsLp = async (
  shortname: string,
  limit: number,
  offset: number
) => {
  return callAPI<PageApiResponse<LPInvestment[]>>(
    `${basePath}/investors/${shortname}/investments-lp?limit=${limit}&offset=${offset}`
  )
}

export const getPeople = async (
  type: InvestorType,
  shortname: string,
  limit: number,
  offset: number
) => {
  return type === 'vc'
    ? getCapitalist(shortname, limit, offset)
    : type === 'cvc'
      ? getCvcPeople(shortname, limit, offset)
      : undefined
}

export const getCapitalist = async (
  shortname: string,
  limit: number,
  offset: number
) => {
  return callAPI<PageApiResponse<People>>(
    `${basePath}/investors/${shortname}/people-capitalist?limit=${limit}&offset=${offset}`
  )
}

export const getCvcPeople = async (
  shortname: string,
  limit: number,
  offset: number
) => {
  return callAPI<PageApiResponse<People>>(
    `${basePath}/investors/${shortname}/people-cvc?limit=${limit}&offset=${offset}`
  )
}

export const getFinanceNews = async (
  shortname: string,
  limit: number,
  offset: number
) => {
  return callAPI<PageApiRespone<InvestorFinanceNews[]>>(
    `${basePath}/investors/${shortname}/news-finance?limit=${limit}&offset=${offset}`
  )
}

export const getExternalNews = async (
  shortname: string,
  limit: number,
  offset: number
) => {
  return callAPI<PageApiRespone<ExternalNews[]>>(
    `${basePath}/investors/${shortname}/news-external?limit=${limit}&offset=${offset}`
  )
}

export const followInvestor = async (shortname: string) => {
  return callAPI<Following>(`${basePath}/investors/${shortname}/follow`, {
    method: 'POST',
    credentials: 'include',
  })
}

export const unfollowInvestor = async (shortname: string) => {
  return callAPI<Following>(`${basePath}/investors/${shortname}/unfollow`, {
    method: 'POST',
    credentials: 'include',
  })
}

export const getInvestorFollowing = async (shortname: string) => {
  return callAPI<Following>(`${basePath}/investors/${shortname}/following`)
}

export const getInvestorFund = async (
  investorShortname: string,
  fundShortname: string
) => {
  return callAPI<ApiResponse<FundDetail>>(
    `${basePath}/investors/${investorShortname}/funds/${fundShortname}`
  )
}

export const getInvestorLpDetail = async (
  investorShortname: string,
  lpDetailShortname: string
) => {
  return callAPI<ApiResponse<LpDetail>>(
    `${basePath}/investors/${investorShortname}/lp-details/${lpDetailShortname}`
  )
}
